import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { categoriesListService } from '../services/categoriesList.service';
import type { CategoryFilters, CategoryType, SettingsCategory } from '../types/categoriesList.types';

const initialFilters: CategoryFilters = {
  query: '',
  type: 'all',
  status: 'all',
};

export function useCategoriesList() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id || '';
  const companyId = profile?.company_id || '';
  const [filters, setFilters] = useState<CategoryFilters>(initialFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [categories, setCategories] = useState<SettingsCategory[]>([]);

  const query = useQuery({
    queryKey: ['settings1', 'categories', 'list', companyId],
    queryFn: () => categoriesListService.getCategories(userId, companyId || null),
    enabled: !!userId,
  });

  useEffect(() => {
    if (query.data) setCategories(query.data);
  }, [query.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesListService.deleteCategory(id, userId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings1', 'categories'] });
      toast.success('Đã xóa danh mục');
    },
    onError: (error: any) => toast.error(`Lỗi khi xóa: ${error.message}`),
  });

  const filteredCategories = useMemo(() => {
    const search = filters.query.trim().toLowerCase();
    return categories.filter((item) => {
      const matchSearch = !search || [item.name, item.code, item.description, item.typeName]
        .some((value) => String(value || '').toLowerCase().includes(search));
      const matchType = filters.type === 'all' || item.type === filters.type;
      const matchStatus = filters.status === 'all' || item.status === filters.status;
      return matchSearch && matchType && matchStatus;
    });
  }, [categories, filters]);

  const selectedCategory = useMemo<SettingsCategory | null>(() => {
    if (selectedId) return categories.find((item) => item.id === selectedId) || null;
    return categories[0] || null;
  }, [categories, selectedId]);

  const summary = useMemo(() => ({
    total: categories.length,
    active: categories.filter((item) => item.status === 'active').length,
    inactive: categories.filter((item) => item.status === 'inactive').length,
    locked: categories.filter((item) => item.status === 'locked').length,
  }), [categories]);

  function deleteCategory(id: string) {
    deleteMutation.mutate(id);
  }

  return {
    categories,
    filteredCategories,
    selectedCategory,
    selectedId: selectedCategory?.id || null,
    filters,
    summary,
    isLoading: query.isLoading || deleteMutation.isPending,
    setFilters,
    setSelectedId,
    deleteCategory,
  };
}
